import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
  Paper,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetSalaryEmployees } from "src/api/salary/employees";
import { formatNumber } from "src/utils";
import { SalaryEmployeeListRow } from "src/types/salary";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { salaryHeadCellSx } from "src/pages/salary/table-styles";
import EmployeeActiveToggle from "./table/table-switch";
import TableActions from "./table/table-actions";

function a11yProps(index: number) {
  return {
    id: `salary-employees-subtab-${index}`,
    "aria-controls": `salary-employees-subtabpanel-${index}`,
  };
}

const EmployeesTab = () => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();

  const [subTab, setSubTab] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const debounceTimer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const tabKey = subTab === 0 ? "active" : "inactive";

  const { data, isLoading, refetch } = useGetSalaryEmployees({
    tab: tabKey,
    page: pageNumber + 1,
    search: debouncedSearch,
  });

  useEffect(() => {
    setPageNumber(0);
  }, [subTab, debouncedSearch]);

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const empty = !isLoading && rows.length === 0;

  const statusLabel = (s: boolean) => {
    if (!s) return t("statusInactive");
    return t("statusActive");
  };

  const tableHead = [
    t("name"),
    t("basicSalary"),
    t("totalAllowance"),
    generalT("balance"),
    generalT("description"),
    generalT("attachments"),
    generalT("status"),
    t("activeInactiveColumn"),
    generalT("actions"),
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/salary/employees/new")}
        >
          {t("addEmployee")}
        </Button>
      </Box>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          minHeight: 44,
          borderBottom: "1px solid #EAECF0",
          mb: 2,
          "& .MuiTab-root": {
            minHeight: 44,
            py: 1.5,
            px: 0,
            mr: 3,
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            color: "#667085",
            "&.Mui-selected": {
              color: "#101828",
            },
          },
        }}
        TabIndicatorProps={{
          sx: {
            height: 2,
            bgcolor: "primary.main",
          },
        }}
      >
        <Tab
          disableRipple
          label={t("employeesList")}
          {...a11yProps(0)}
        />
        <Tab
          disableRipple
          label={t("statusInactive")}
          {...a11yProps(1)}
        />
      </Tabs>

      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <TextField
          size="small"
          placeholder={t("searchByName")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {tableHead.map((item) => (
                <TableCell key={item} align="center" sx={salaryHeadCellSx}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={8} cellsLength={tableHead.length} />
          ) : (
            <TableBody>
              {rows.map((row: SalaryEmployeeListRow) => (
                <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell align="center">
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="primary.main"
                      fontSize={14}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {formatNumber(row.basic_salary)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {formatNumber(row.total_allowance)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {formatNumber((row.basic_salary ?? 0) + (row.total_allowance ?? 0))}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row.description ?? ""}
                  >
                    {row.description?.trim() ? row.description : "-"}
                  </TableCell>
                  <TableCell align="center">{row.attachments_count ?? 0}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={statusLabel(row.status)}
                      size="small"
                      variant="outlined"
                      color={row.status ? "success" : "default"}
                      sx={{ height: 22 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <EmployeeActiveToggle row={row} refetch={refetch} />
                  </TableCell>
                  <TableCell align="center">
                    <TableActions row={row} refetch={refetch} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {empty && <NoData />}
      </TableContainer>

      <TablePagination
        sx={{ mt: 2, border: 0, display: "flex", justifyContent: "flex-end" }}
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={(_, p) => setPageNumber(p)}
        showFirstButton
        showLastButton
      />

    </Box>
  );
};

export default EmployeesTab;
